import React from 'react';

import {assert} from 'Asserts';
import {
  SchemaValidationShortDisplayTextByStatus,
  SchemaValidationStatus,
} from 'schemas/SchemaValidationConstants';
import {FILTER_VALUE_SEPARATOR, FilterKey, MultiValueFilter} from 'search/SearchConstants';
import {OptionData} from 'ui-library/menus/MenuConstants';
import pick from 'util/pick';

import DropdownFilterForm from './base/DropdownFilterForm';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';

export interface SchemaValidationStatusFilter extends MultiValueFilter {
  key: FilterKey.SCHEMA_VALIDATION_STATUS | FilterKey.INVENTORY_VALIDATION_STATUS;
}
interface Props extends BaseFilterFormProps {
  filter: SchemaValidationStatusFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: SchemaValidationStatusFilter) => void;
  onRemove?: () => void;
}

function getStatusOptions(
  filterKey: FilterKey.SCHEMA_VALIDATION_STATUS | FilterKey.INVENTORY_VALIDATION_STATUS
): Array<OptionData> {
  const options = [
    {
      // The filter is implemented as a multi-select, separated by FILTER_VALUE_SEPARATOR.
      // Users consider both PASSED and OVERRIDDEN statuses as valid bioentities.
      value: toString([SchemaValidationStatus.PASSED, SchemaValidationStatus.OVERRIDDEN]),
      name: SchemaValidationShortDisplayTextByStatus.PASSED,
    },
    {
      value: toString([SchemaValidationStatus.FAILED]),
      name: SchemaValidationShortDisplayTextByStatus.FAILED,
    },
  ];
  if (filterKey !== FilterKey.INVENTORY_VALIDATION_STATUS) {
    options.push({
      value: toString([SchemaValidationStatus.VALIDATING]),
      name: SchemaValidationShortDisplayTextByStatus.VALIDATING,
    });
  }
  return options;
}

function toString(array: Array<string>): string {
  return array.join(FILTER_VALUE_SEPARATOR);
}

function fromString(string: string): Array<string> {
  return string.split(FILTER_VALUE_SEPARATOR);
}

const SchemaValidationStatusFilterForm: React.SFC<Props> = (props: Props): JSX.Element => {
  const skeletonProps = pick(props, ...BASE_FILTER_FORM_PROP_NAMES);
  const statusOptions = getStatusOptions(props.filter.key);
  assert(
    props.filterStyle === FilterStyle.SECONDARY,
    'Schema validation status filter is only configured to work as a secondary filter'
  );
  return (
    <DropdownFilterForm
      {...skeletonProps}
      filterStyle={props.filterStyle}
      isMulti={false}
      onChangeValue={(stringValue: string) => {
        props.onChangeFilter({...props.filter, value: fromString(stringValue)});
      }}
      onRemove={props.onRemove}
      optionDatas={statusOptions}
      value={toString(props.filter.value)}
    />
  );
};

export default SchemaValidationStatusFilterForm;
